import { useEffect, useRef, useState } from 'react'
import { PALETTE_ACTIVITIES_QUERY, PALETTE_CONTACTS_QUERY, PALETTE_MESSAGES_QUERY } from './graphql'
import { activitiesSearchTerm, contactsSearchTerm, isSearchable, parsePaletteQuery } from './paletteSearch'
import { contactPhones, latestPerThread, threadsSearchTerm, type PaletteThread } from './paletteThreads'
import type {
  Activity,
  ActivityListResult,
  Contact,
  ContactListResult,
  Message,
  MessageListResult,
} from 'app/generated/graphql'
import { gqlQuery } from 'app/lib/graphqlClient'
import { bg } from 'lib/background'
import { logError } from 'lib/logging'

export const SEARCH_DEBOUNCE_MS = 200
export const CONTACT_LIMIT = 5
export const ACTIVITY_LIMIT = 3
export const THREAD_LIMIT = 3
/** Newest messages fetched before collapsing to one per thread; a busy thread can own many. */
export const MESSAGE_FETCH_LIMIT = 30

type Failures = { contacts: boolean; activities: boolean; threads: boolean }

export type GlobalSearchState = {
  contacts: Contact[]
  activities: Activity[]
  threads: PaletteThread[]
  fetching: boolean
  failed: Failures
}

/** What the last completed round of requests returned, and which raw query it answers. */
type SettledState = {
  contacts: Contact[]
  activities: Activity[]
  threads: PaletteThread[]
  failed: Failures
  forQuery: string
}

const NO_FAILURES: Failures = { contacts: false, activities: false, threads: false }

const NOTHING_SETTLED: SettledState = { contacts: [], activities: [], threads: [], failed: NO_FAILURES, forQuery: `` }

type GroupResult<T> = { hits: T[]; failed: boolean }

async function fetchGroup<T, R>(
  label: `contacts` | `activities` | `messages`,
  document: Parameters<typeof gqlQuery>[0],
  variables: Record<string, unknown>,
  extract: (data: R) => T[] | undefined,
): Promise<GroupResult<T>> {
  const result = await gqlQuery<R>(document, variables)
  if (result.error || !result.data) {
    logError(`Global search: ${label} request failed`, result.error?.message)
    return { hits: [], failed: true }
  }
  return { hits: extract(result.data) ?? [], failed: false }
}

/**
 * Debounced contacts + activities lookup for the palette. Each keystroke bumps a request
 * id; a response for any earlier id is discarded, so fast typing never shows results for
 * text that is no longer in the box. Previous results stay on screen while a new request
 * is in flight.
 */
export function useGlobalSearch(rawQuery: string, activitiesExclusion: string): GlobalSearchState {
  const [settled, setSettled] = useState<SettledState>(NOTHING_SETTLED)
  const requestIdRef = useRef(0)
  const searchable = isSearchable(parsePaletteQuery(rawQuery))

  useEffect(() => {
    const query = parsePaletteQuery(rawQuery)
    requestIdRef.current += 1
    if (!isSearchable(query)) return
    const requestId = requestIdRef.current

    const timer = setTimeout(() => {
      bg(async () => {
        const [contacts, activities] = await Promise.all([
          fetchGroup<Contact, { contacts?: ContactListResult }>(
            `contacts`,
            PALETTE_CONTACTS_QUERY,
            {
              search: contactsSearchTerm(query),
              pagination: { limit: CONTACT_LIMIT },
              sort: { field: `lastCommDate`, direction: `DESC` },
            },
            (data) => data.contacts?.hits,
          ),
          fetchGroup<Activity, { activities?: ActivityListResult }>(
            `activities`,
            PALETTE_ACTIVITIES_QUERY,
            {
              search: activitiesSearchTerm(query, activitiesExclusion),
              pagination: { limit: ACTIVITY_LIMIT },
              sort: { field: `activityDate`, direction: `DESC` },
            },
            (data) => data.activities?.hits,
          ),
        ])
        if (requestId !== requestIdRef.current) return

        // Threads are keyed by phone, not name, so they wait for the contacts round: the
        // numbers of every matched contact plus any typed digit run go to the message index.
        const threadSearch = threadsSearchTerm(contactPhones(contacts.hits), query.digits)
        const messages: GroupResult<Message> = threadSearch
          ? await fetchGroup<Message, { messages?: MessageListResult }>(
              `messages`,
              PALETTE_MESSAGES_QUERY,
              {
                search: threadSearch,
                pagination: { limit: MESSAGE_FETCH_LIMIT },
                sort: { field: `createdDate`, direction: `DESC` },
              },
              (data) => data.messages?.hits,
            )
          : { hits: [], failed: false }
        if (requestId !== requestIdRef.current) return

        setSettled({
          contacts: contacts.hits,
          activities: activities.hits,
          threads: latestPerThread(messages.hits, contacts.hits, THREAD_LIMIT),
          failed: { contacts: contacts.failed, activities: activities.failed, threads: messages.failed },
          forQuery: rawQuery,
        })
      })
    }, SEARCH_DEBOUNCE_MS)

    return () => clearTimeout(timer)
  }, [rawQuery, activitiesExclusion])

  return {
    contacts: searchable ? settled.contacts : [],
    activities: searchable ? settled.activities : [],
    threads: searchable ? settled.threads : [],
    // Derived during render, not stored: setting it from the effect lands a frame late,
    // so the keystroke that crosses the search threshold painted the no-match line first.
    fetching: searchable && settled.forQuery !== rawQuery,
    failed: searchable ? settled.failed : NO_FAILURES,
  }
}
